import React, { useState, useEffect } from 'react';

type FilterType = {
  [key: string]: { group: string; label: string; query: string };
};

type FacetType = {
  [key: string]: { label: string; query: string };
};

type SortType = {
  [key: string]: { label: string; field: string };
};

export type HierarchicalFacetValue = {
  CN: string;
  term: string;
};

type HierarchicalFacetType = {
  [key: string]: {
    label: string;
    CN: string;
  };
};

interface SearchValue {
  source: string;
  text: string;
}

export type SearchType = {
  identifier: string;
  field: string;
  searchValues: SearchValue[];
};

interface CustomSearchCriterion {
  field: string;
  fieldLabel: string;
  searchValues: string[];
  description: string;
}

interface BasketFilter {
  basketId: string;
  basketName: string;
}

export class Search implements SearchType {
  public identifier: string;
  public field: string;
  public searchValues: SearchValue[];

  constructor(identifier: string, field: string, searchValues: SearchValue[] = []) {
    this.identifier = identifier;
    this.field = field;
    this.searchValues = searchValues;
  }
}

class CC {
  public Searches: Search[] = [];
  matchAllCriteria: boolean = true;
  public filters: string[] = []; // This will store the filter keys the user selects
  public facets: { [field: string]: string[] } = {}; // This will store the facet fields the user selects
  public hierarchicalFacets: { [key: string]: HierarchicalFacetValue[] } = {};
  public customSearchCriteria: CustomSearchCriterion[] = [];
  public basketFilters: BasketFilter[] = [];

  constructor() {}
  
}

class CcConfig {

  simpleSearchFields: {
    [key: string]: {
      field: string;
      browse: boolean;
    };
  } = {
    "All search fields": {
      field: "*",
      browse: false,
    },
    "Object Number": {
      field: "/record/objectnumber",
      browse: true,
    },
    "Classification": {
      field: "/record/classification",
      browse: true,
    },
    "Object name": {
      field: "/record/objectname",
      browse: true,
    },
    "Alphasort": {
      field: "/record/alphasort",
      browse: true,
    },
    "Materials and techniques": {
      field: "/record/medium",
      browse: true,
    },
    "Dated": {
      field: "/record/datebegin",
      browse: true,
    },
    // Date from - till is working fine if you add a second DateEnd Field and doe search with < and >
    // "Dated end": {
      // field: "/record/dateend",
      // browse: true,
    // },
    "Title": {
      field: "/record/title",
      browse: true,
    },
    "Inscriptions": {
      field: "/record/inscribed",
      browse: true,
    },
  };

  //advancedSearchFields is not being used anymore!
  advancedSearchFields: {
    [key: string]: {
      field: string;
      browse: boolean;
    };
  } = {
    "Title": {
      field: "/record/title",
      browse: true,
    },
    "Object Number": {
      field: "/record/objectnumber",
      browse: true,
    },
    "Personnes et institutions": {
      field: "/record/sortname",
      browse: true,
    },
    "Dated": {
      field: "/record/datebegin,/record/dated,/record/dateend",
      browse: false,
    },
    "Materials and techniques": {
      field: "/record/thesmaterial",
      browse: true,
    },
    "Nature de l'objet": {
      field: "/record/classification",
      browse: true,
    },
    "Inscriptions": {
      field: "/record/inscribed",
      browse: false,
    },
    "Provenance": {
      field: "/record/provenance",
      browse: false,
    },
    "Date de l'acquisition": {
      field: "/record/accessiondate",
      browse: false,
    },
    "Emplacement actuel": {
      field: "/record/location/site",
      browse: true,
    },
    "Exposition permanente": {
      field: "/record/locmusseecollection",
      browse: true,
    },
    "Objets hors les murs": {
      field: "/record/objoutside",
      browse: true,
    },
    "Paniers": {
      field: "/record/collection",
      browse: true,
    }
  };

  mobileSearchFields: {
    [key: string]: {
      field: string;
      browse: boolean;
    };
  } = {
    "All search fields": {
      field: "*",
      browse: false,
    },
    "Object Number": {
      field: "/record/objectnumber",
      browse: true,
    },
    "Classification": {
      field: "/record/classification",
      browse: true,
    },
    "Object name": {
      field: "/record/objectname",
      browse: true,
    },
    "Alphasort": {
      field: "/record/alphasort",
      browse: true,
    },
    "Materials and techniques": {
      field: "/record/medium",
      browse: true,
    },
    "Dated": {
      field: "/record/datebegin",
      browse: true,
    },    
    "Subject / theme": {
      field: "/record/thesthematique",
      browse: true,
    }, 
    "Title": {
      field: "/record/title",
      browse: true,
    }, 
    "Place event": {
      field: "/record/theslieuevenement",
      browse: true,
    },
    "Place interview": {
      field: "/record/theslieuinterview",
      browse: true,
    },
    "Description": {
      field: "/record/inscribed",
      browse: true,
    },
  };

  filters: FilterType = {
    filter_objects: { group: "A", label: "Uniquement avec images", query: "/record/avecimage=Uniquement avec images" },
    filter_icono: { group: "A", label: "Iconography", query: "/Record/source=icono" },
    filter_freeOfRights: { group: "B", label: "Free to use Images", query: "CN=[ICO.AAN.AAB.AAC]" },
  };

  facets: FacetType = {
    institution: { label: "facet_institution", query: "/record/institutions" },
    recordtype: { label: "facet_recordtype", query: "/record/recordtypeshow" },
    classification: { label: "facet_classification", query: "/record/classification" },
    objectname: { label: "facet_objectname", query: "/record/objectname" },
    creator: { label: "facet_creator", query: "/record/sortname" },
  };

  hierarchicalFacets: HierarchicalFacetType = {
    fonds: { label: "facet_fonds", CN: "MVW.AAX"},
    categorie: { label: "facet_categorie", CN: "MVW.ABE"},
    theme: { label: "facet_theme", CN: "MVW.AAJ"},
    place: { label: "facet_place", CN: "MVW.AAL.AAC"},
  }

  sort: SortType = {
    recordtype: {label: "Record type", field: "/record/recordtypeshow"},
    objectnumber: {label: "Object number (collections)", field: "/record/objectnumber"},
    institution: {label: "Institution (collections)", field: "/record/institutions"},
    title: {label: "Title (collections)", field: "/record/title"},
    objectname: {label: "Object name (collections)", field: "/record/objectname"},
    date: {label: "Dated  (collections)", field: "/record/datebegin"},
    classification: {label: "Classification (collections)", field: "/record/classification"},
    displayname: {label: "Display name (persons and institutions)", field: "/record/displayname"},
    sortname: {label: "Sort name (persons and institutions)", field: "/record/alphasort"},    
    datepi: {label: "Dated (persons and institutions)", field: "/record/begindate"},
    exhibitiontitle: {label: "Exhibition title (expositions)", field: "/record/exhtitle"},
    sitename: {label: "Site name (expositions)", field: "/record/institution"},
    dateexh: {label: "Date exhibition (expositions)", field: "/record/beginsortdate"},
    relevance: {label: "Relevance", field: "ccRelevance"},
  }

}

let ccConfig = new CcConfig();

export { CC, ccConfig };
